"use client"

import { useState, useEffect, useCallback, useRef } from 'react'
import { saveYearlyReview, loadYearlyReview } from '../service'
import { showErrorToast } from '@/lib/utils/error-toast'

export interface YearlyReviewResponses {
  // The Overall Frame
  "year-was"?: string
  
  // Claiming Your Wins
  "quiet-wins"?: string
  "let-go"?: string
  "chose-myself"?: string
  "progress"?: string
  
  // The Year as a Journey
  "turning-points"?: string
  "what-changed"?: string
  "hardest-moments"?: string
  
  // Energy & Well-Being
  "gave-energy"?: string
  "drained-energy"?: string
  "body-felt"?: string
  "helped-feel-better"?: string
  
  // Relationships & Environment
  "who-supported"?: string
  "who-drifted"?: string
  "who-lesson"?: string
  "me-in-relationships"?: string
  
  // Finances
  "income-trend"?: string
  "income-sources"?: string
  "money-feelings"?: string
  "assets"?: string
  
  // Memory & Life
  "warmest-moment"?: string
  "most-alive"?: string
  "truly-myself"?: string
  
  // Closing the Year
  "letting-go"?: string
  "grateful-to-myself"?: string
  "message-to-self"?: string
  
  // Custom sections (extensible)
  [key: string]: any
}

interface UseReviewPersistenceOptions {
  email: string | null
  reviewYear: number
  initialData?: YearlyReviewResponses
}

export function useReviewPersistence({
  email,
  reviewYear,
  initialData = {},
}: UseReviewPersistenceOptions) {
  const [responses, setResponses] = useState<YearlyReviewResponses>(initialData)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [isLoading, setIsLoading] = useState(true)
  
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<string | null>(null)

  // LocalStorage key
  const getStorageKey = () => {
    if (!email) return `yearly_review_anonymous_${reviewYear}`
    return `yearly_review_${email}_${reviewYear}`
  }

  // Load from localStorage on mount
  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const key = getStorageKey()
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed = JSON.parse(stored)
          // Use stored data immediately for instant display
          setResponses(parsed)
          setIsLoading(false)
        } else {
          setIsLoading(false)
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e)
        setIsLoading(false)
      }
    }

    loadFromStorage()
  }, [email, reviewYear])

  // Load from server (after localStorage for instant UX)
  useEffect(() => {
    // Only load if email is set and not loading from localStorage
    if (!email || isLoading) return

    const loadFromServer = async () => {
      try {
        // Get code from URL if present
        const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
        const code = urlParams?.get('code') || undefined
        
        const result = await loadYearlyReview(email, reviewYear, code)
        
        // Handle both error response format and thrown errors
        if (result?.error) {
          // Don't log 401/400 errors for anonymous users without email - this is expected
          if (result.error.includes('required') || result.error.includes('Authentication')) {
            return
          }
          console.error('Error loading from server:', result.error)
          return
        }

        if (result?.data?.responses) {
          // Merge server data with localStorage (server wins on conflicts)
          setResponses(prev => ({
            ...prev,
            ...result.data.responses,
          }))
          // Update localStorage with server data
          saveToStorage(result.data.responses)
        }
      } catch (e) {
        // Only log unexpected errors, not expected auth/email errors
        const errorMessage = e instanceof Error ? e.message : String(e)
        if (!errorMessage.includes('required') && !errorMessage.includes('Authentication')) {
          console.error('Error loading from server:', e)
        }
      }
    }

    loadFromServer()
  }, [email, reviewYear, isLoading])

  // Save to localStorage (immediate, synchronous)
  const saveToStorage = useCallback((data: YearlyReviewResponses) => {
    try {
      const key = getStorageKey()
      localStorage.setItem(key, JSON.stringify(data))
    } catch (e) {
      console.error('Error saving to localStorage:', e)
    }
  }, [email, reviewYear])

  // Save to server (debounced, async)
  const saveToServer = useCallback(async (data: YearlyReviewResponses) => {
    if (!email) return

    // Validate email format before sending
    if (!email.includes('@')) {
      setSaveStatus('error')
      return
    }

    setSaveStatus('saving')
    
    try {
      const result = await saveYearlyReview(email, reviewYear, data, 'draft')
      
      // Handle both success format and error format
      if (result?.success === true) {
        setSaveStatus('saved')
        lastSavedRef.current = JSON.stringify(data)
        
        // Clear status after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000)
      } else if (result?.error) {
        // Error response from Edge Function
        setSaveStatus('error')
        // Show detailed error to user
        showErrorToast({
          title: "Failed to save",
          description: result.error || "Could not save your reflection. Your changes are saved locally and will be synced when connection is restored.",
          context: "Yearly reflection autosave",
          error: result.error
        })
        // Clear error status after 5 seconds (longer so user can see it)
        setTimeout(() => setSaveStatus('idle'), 5000)
      } else {
        // Unexpected response format
        setSaveStatus('error')
        showErrorToast({
          title: "Failed to save",
          description: "Could not save your reflection. Your changes are saved locally and will be synced when connection is restored.",
          context: "Yearly reflection autosave"
        })
        setTimeout(() => setSaveStatus('idle'), 5000)
      }
    } catch (e) {
      setSaveStatus('error')
      // Show detailed error to user
      const errorMessage = e instanceof Error ? e.message : 'Could not save your reflection. Your changes are saved locally and will be synced when connection is restored.'
      showErrorToast({
        title: "Failed to save",
        description: errorMessage,
        context: "Yearly reflection autosave",
        error: e
      })
      // Clear error status after 5 seconds (longer so user can see it)
      setTimeout(() => setSaveStatus('idle'), 5000)
      // Don't throw - localStorage already saved, user can retry
    }
  }, [email, reviewYear])

  // Update responses (triggers auto-save)
  const updateResponses = useCallback((updates: Partial<YearlyReviewResponses>) => {
    setResponses(prev => {
      const updated = { ...prev, ...updates }
      
      // Save to localStorage immediately
      saveToStorage(updated)
      
      // Debounce server save (30 seconds or on blur)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        saveToServer(updated)
      }, 30000) // 30 seconds
      
      return updated
    })
  }, [saveToStorage, saveToServer])

  // Force immediate save (called on blur, before navigation, etc.)
  const flushSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    
    const currentData = responses
    const currentDataString = JSON.stringify(currentData)
    
    // Only save if data changed since last save
    if (currentDataString !== lastSavedRef.current) {
      await saveToServer(currentData)
    }
  }, [responses, saveToServer])

  // Save before page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Try to save synchronously (may not work in all browsers)
      const currentData = responses
      saveToStorage(currentData)
      
      // Warn user if there are unsaved changes
      const currentDataString = JSON.stringify(currentData)
      if (currentDataString !== lastSavedRef.current && email) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [responses, email, saveToStorage])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  return {
    responses,
    updateResponses,
    flushSave,
    saveStatus,
    isLoading,
  }
}

